import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Calendar as CalendarIcon, FileText, Clock, Save, X, Check, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_LEAD_SCHEMAS } from "@/lib/leads/leadSchemas";
import { useAgentPerformance } from "@/hooks/useAgentPerformance";
import { useAgent } from "@/contexts/AgentContext";
import { toast } from "sonner";

interface LeadRow {
  id: string;
  lead_type_name: string;
  lead_type_id: string;
  vertical: string;
  state: string;
  status: string;
  created_at: number;
  lead_profile: Record<string, unknown> | null;
  agent_status: string | null;
  agent_notes: string | null;
  agent_contacted_at: number | null;
  follow_up_at: number | null;
  disposition: string | null;
  routed_to_agent_id: string | null;
}

const PIPELINE_STATUSES = [
  { value: "new", label: "New", description: "Lead just received" },
  { value: "contacted", label: "Contacted", description: "First contact made" },
  { value: "no_answer", label: "No Answer", description: "Attempted contact, no response" },
  { value: "qualified", label: "Qualified", description: "Lead is a good fit" },
  { value: "not_qualified", label: "Not Qualified", description: "Lead doesn't meet criteria" },
  { value: "proposal_sent", label: "Proposal Sent", description: "Quote or proposal delivered" },
  { value: "won", label: "Won", description: "Deal closed successfully" },
  { value: "lost", label: "Lost", description: "Deal lost to competitor or declined" },
] as const;

const pipelineStyle: Record<string, { label: string; className: string }> = {
  new: { label: "New", className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800" },
  contacted: { label: "Contacted", className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800" },
  no_answer: { label: "No Answer", className: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800" },
  qualified: { label: "Qualified", className: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800" },
  not_qualified: { label: "Not Qualified", className: "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700" },
  proposal_sent: { label: "Proposal Sent", className: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-800" },
  won: { label: "Won", className: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800" },
  lost: { label: "Lost", className: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800" },
};

const DISPOSITIONS = [
  { value: "not_interested", label: "Not Interested" },
  { value: "wrong_number", label: "Wrong Number" },
  { value: "duplicate", label: "Duplicate Lead" },
  { value: "bad_data", label: "Bad Data / Invalid" },
  { value: "chose_competitor", label: "Chose Competitor" },
  { value: "price_too_high", label: "Price Too High" },
  { value: "not_eligible", label: "Not Eligible" },
  { value: "other", label: "Other" },
] as const;

const verticalLabels: Record<string, string> = {
  life_insurance: "Life Insurance",
  mortgage: "Mortgage",
};

const CONTACT_FIELDS = new Set(["first_name", "last_name", "phone", "email", "state"]);
const HIDDEN_FIELDS = new Set(["utm_source", "utm_medium", "utm_campaign", "utm_content", "otp_verified"]);

// Lovable deployed agent_status with default 'pending' instead of 'new'.
function normalizeAgentStatus(raw: string | null): string {
  if (!raw || raw === "pending") return "new";
  return raw;
}

function getLeadName(profile: Record<string, unknown> | null): string {
  if (!profile) return "Unknown";
  const first = String(profile.first_name || "").trim();
  const last = String(profile.last_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return "Unknown";
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatFollowUpDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const LeadDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [lead, setLead] = useState<LeadRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const { recalculate: recalcPerformance } = useAgentPerformance();
  const { state: agentState } = useAgent();
  const navigate = useNavigate();

  // Pipeline state
  const [agentStatus, setAgentStatus] = useState<string>("new");
  const [agentNotes, setAgentNotes] = useState("");
  const [followUpAt, setFollowUpAt] = useState<Date | undefined>(undefined);
  const [followUpHour, setFollowUpHour] = useState<string>("09:00");
  const [disposition, setDisposition] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const notesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchLead = async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("id, lead_type_name, lead_type_id, vertical, state, status, created_at, lead_profile, agent_status, agent_notes, agent_contacted_at, follow_up_at, disposition, routed_to_agent_id")
        .eq("id", id)
        .maybeSingle();

      if (error) {
        console.error("[LeadDetail] Failed to fetch lead:", error);
      }
      const row = data as LeadRow | null;

      // S1: Defense-in-depth — verify the current agent owns this lead
      const currentAgentId = agentState.profile.id;
      if (row && currentAgentId && row.routed_to_agent_id !== currentAgentId) {
        console.warn(`[LeadDetail] Access denied: agent ${currentAgentId} attempted to view lead ${id} owned by ${row.routed_to_agent_id}`);
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      setLead(row);
      if (row) {
        setAgentStatus(normalizeAgentStatus(row.agent_status));
        setAgentNotes(row.agent_notes ?? "");
        setDisposition(row.disposition ?? "");
        if (row.follow_up_at) {
          const d = new Date(row.follow_up_at);
          setFollowUpAt(d);
          const hh = d.getHours().toString().padStart(2, "0");
          const mm = d.getMinutes().toString().padStart(2, "0");
          setFollowUpHour(`${hh}:${mm}`);
        } else {
          setFollowUpAt(undefined);
        }
      }
      setLoading(false);
    };

    fetchLead();
  }, [id, agentState.profile.id]);

  const saveField = useCallback(async (updates: Record<string, unknown>) => {
    if (!id) return;
    setSaving(true);

    // Only set agent_contacted_at on first interaction (not every save)
    const isFirstContact = lead && !lead.agent_contacted_at;
    const payload: Record<string, unknown> = { ...updates };
    if (isFirstContact) {
      payload.agent_contacted_at = Date.now();
    }

    const attemptUpdate = async (): Promise<{ data: unknown[] | null; error: unknown }> => {
      return supabase
        .from("leads")
        .update(payload)
        .eq("id", id)
        .select("id");
    };

    let { data, error } = await attemptUpdate();

    if (!error && (!data || (data as unknown[]).length === 0)) {
      // RLS blocked the update — try resolving agent identity and reclaiming orphaned leads
      console.warn("[LeadDetail] Update returned 0 rows — attempting identity resolution + orphan reclaim");
      try {
        // Step 1: Resolve identity (handles webhook-created agents + email sync)
        const { data: resolved } = await (supabase.rpc as any)("resolve_agent_identity");
        console.log("[LeadDetail] resolve_agent_identity result:", resolved);

        // Step 2: If resolve didn't fix it, try explicit orphan reclaim
        // (handles accounts that were deleted and re-created)
        if (!resolved?.reclaim || resolved.reclaim.leads_reclaimed === 0) {
          const { data: reclaimed } = await (supabase.rpc as any)("reclaim_orphaned_leads");
          console.log("[LeadDetail] reclaim_orphaned_leads result:", reclaimed);
        }

        // Retry the update after both recovery attempts
        const retry = await attemptUpdate();
        data = retry.data;
        error = retry.error;
      } catch {
        // RPCs not available — fall through to error
      }
    }

    if (error) {
      console.error("[LeadDetail] Failed to save:", error);
      toast.error("Failed to save changes");
    } else if (!data || (data as unknown[]).length === 0) {
      console.error("[LeadDetail] Update returned 0 rows after identity resolution — RLS policy mismatch");
      toast.error("Permission denied — changes were not saved. Try refreshing the page.");
    } else {
      // Update local lead state to prevent stale data
      setLead((prev) =>
        prev
          ? { ...prev, ...(payload as Partial<LeadRow>) }
          : prev
      );
      toast.success("Saved");
    }
    setSaving(false);
  }, [id, lead]);

  const handleStatusChange = useCallback((value: string) => {
    setAgentStatus(value);
    // Clear disposition when moving away from lost/not_qualified
    const needsDisposition = value === "lost" || value === "not_qualified";
    if (!needsDisposition) {
      setDisposition("");
      saveField({ agent_status: value, disposition: null }).then(() => recalcPerformance());
    } else {
      saveField({ agent_status: value }).then(() => recalcPerformance());
    }
  }, [saveField, recalcPerformance]);

  const handleDispositionChange = useCallback((value: string) => {
    setDisposition(value);
    saveField({ disposition: value });
  }, [saveField]);

  const handleNotesChange = useCallback((value: string) => {
    setAgentNotes(value);
    setNotesDirty(true);
    // Debounce auto-save: 2 seconds after last keystroke
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(() => {
      saveField({ agent_notes: value || null });
      setNotesDirty(false);
    }, 2000);
  }, [saveField]);

  const handleNotesSaveNow = useCallback(() => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    saveField({ agent_notes: agentNotes || null });
    setNotesDirty(false);
  }, [saveField, agentNotes]);

  const buildFollowUpTimestamp = useCallback((date: Date, time: string) => {
    const followUp = new Date(date);
    const [hh, mm] = time.split(":").map(Number);
    followUp.setHours(hh, mm, 0, 0);
    return followUp.getTime();
  }, []);

  const handleFollowUpChange = useCallback((date: Date | undefined) => {
    if (date) {
      const ts = buildFollowUpTimestamp(date, followUpHour);
      setFollowUpAt(new Date(ts));
      saveField({ follow_up_at: ts, follow_up_reminded: false });
    } else {
      setFollowUpAt(undefined);
      saveField({ follow_up_at: null, follow_up_reminded: false });
    }
  }, [saveField, followUpHour, buildFollowUpTimestamp]);

  const handleFollowUpTimeChange = useCallback((time: string) => {
    setFollowUpHour(time);
    if (followUpAt) {
      const ts = buildFollowUpTimestamp(followUpAt, time);
      setFollowUpAt(new Date(ts));
      saveField({ follow_up_at: ts, follow_up_reminded: false });
    }
  }, [saveField, followUpAt, buildFollowUpTimestamp]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    };
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading lead...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (accessDenied || !lead) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Link to="/dashboard/leads" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </Link>
          <div className="rounded-2xl border bg-card p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">Lead not found</h2>
            <p className="text-muted-foreground">This lead may have been removed or you don't have access to it.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const profile = (lead.lead_profile ?? {}) as Record<string, unknown>;
  const name = getLeadName(profile);
  const phone = String(profile.phone || "");
  const email = String(profile.email || "");
  const currentStyle = pipelineStyle[agentStatus] ?? pipelineStyle.new;
  const showDisposition = agentStatus === "lost" || agentStatus === "not_qualified";
  const isFollowUpDue = followUpAt ? followUpAt.getTime() <= Date.now() : false;

  const schema = DEFAULT_LEAD_SCHEMAS.find((s) => s.leadTypeId === lead.lead_type_id);
  const surveyFields = schema
    ? schema.fields.filter((f) => !CONTACT_FIELDS.has(f.key) && !HIDDEN_FIELDS.has(f.key))
    : Object.keys(profile)
        .filter((k) => !CONTACT_FIELDS.has(k) && !HIDDEN_FIELDS.has(k))
        .map((k) => ({ key: k, label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }));

  const phoneDigits = phone.replace(/\D/g, "");
  const phoneFormatted = phoneDigits.length === 10
    ? `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`
    : phone;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back Button */}
        <Link to="/dashboard/leads" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Leads
        </Link>

        {/* Lead Header */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
                {getInitials(name)}
              </div>
              <div>
                <h1 className="font-display text-2xl font-bold text-foreground">{name}</h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {lead.lead_type_name}
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {lead.state}
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span>{verticalLabels[lead.vertical] ?? lead.vertical}</span>
                </div>
              </div>
            </div>

            <span className={`px-4 py-2 rounded-full text-sm font-medium border ${currentStyle.className}`}>
              {currentStyle.label}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" />
              Received: {formatDate(lead.created_at)}
            </span>
            {isFollowUpDue && followUpAt && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                <Clock className="w-4 h-4" />
                Follow-up due: {formatFollowUpDate(followUpAt.getTime())}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Info */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="font-display text-lg font-semibold text-foreground mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {phone && (
                  <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <a href={`tel:${phoneDigits}`} className="font-medium text-foreground hover:underline">
                        {phoneFormatted}
                      </a>
                    </div>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <a href={`mailto:${email}`} className="font-medium text-foreground hover:underline">
                        {email}
                      </a>
                    </div>
                  </div>
                )}
                {!phone && !email && (
                  <p className="text-sm text-muted-foreground col-span-2">No contact information available.</p>
                )}
              </div>
            </div>

            {/* Survey Answers */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="font-display text-lg font-semibold text-foreground mb-4">Survey Answers</h2>
              <div className="space-y-3">
                {surveyFields.map((field) => {
                  const value = profile[field.key];
                  if (value === undefined || value === null || value === "") return null;
                  const displayValue = typeof value === "boolean"
                    ? (value ? "Yes" : "No")
                    : String(value);
                  return (
                    <div key={field.key} className="flex justify-between items-start py-3 border-b border-border last:border-0">
                      <span className="text-sm text-muted-foreground">{field.label}</span>
                      <span className="text-sm font-medium text-foreground text-right max-w-[60%]">{displayValue}</span>
                    </div>
                  );
                })}
                {surveyFields.every((f) => {
                  const v = profile[f.key];
                  return v === undefined || v === null || v === "";
                }) && (
                  <p className="text-sm text-muted-foreground">No survey data available for this lead.</p>
                )}
              </div>
            </div>

            {/* Agent Notes */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-semibold text-foreground">Notes</h2>
                <div className="flex items-center gap-2">
                  {notesDirty && (
                    <Button variant="ghost" size="sm" onClick={handleNotesSaveNow} disabled={saving}>
                      <Save className="w-3.5 h-3.5 mr-1.5" />
                      Save
                    </Button>
                  )}
                  {saving && <span className="text-xs text-muted-foreground">Saving...</span>}
                  {!saving && !notesDirty && agentNotes && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Check className="w-3 h-3" /> Saved
                    </span>
                  )}
                </div>
              </div>
              <Textarea
                placeholder="Add notes about this lead... (auto-saves after 2s)"
                value={agentNotes}
                onChange={(e) => handleNotesChange(e.target.value)}
                className="min-h-[120px] resize-y"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Pipeline Status */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="font-display text-lg font-semibold text-foreground mb-4">Pipeline Status</h2>
              <div className="space-y-4">
                <Select value={agentStatus} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPELINE_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex flex-col">
                          <span>{s.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Status description */}
                <p className="text-xs text-muted-foreground">
                  {PIPELINE_STATUSES.find((s) => s.value === agentStatus)?.description}
                </p>

                {/* Disposition (for lost/not_qualified) */}
                {showDisposition && (
                  <div className="pt-2 border-t border-border">
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Reason
                    </label>
                    <Select value={disposition} onValueChange={handleDispositionChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a reason..." />
                      </SelectTrigger>
                      <SelectContent>
                        {DISPOSITIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Follow-up */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="font-display text-lg font-semibold text-foreground mb-4">Follow-up</h2>
              <div className="space-y-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        !followUpAt ? "text-muted-foreground" : ""
                      } ${isFollowUpDue ? "border-amber-400 text-amber-600 dark:text-amber-400" : ""}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {followUpAt ? formatFollowUpDate(followUpAt.getTime()) : "Set follow-up date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={followUpAt}
                      onSelect={handleFollowUpChange}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {followUpAt && (
                  <>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <Select value={followUpHour} onValueChange={handleFollowUpTimeChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => {
                            const h = Math.floor((i + 14) / 2) % 24;
                            const m = (i % 2) * 30;
                            const value = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                            const ampm = h >= 12 ? "PM" : "AM";
                            const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                            const label = `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
                            return <SelectItem key={value} value={value}>{label}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You'll receive a reminder via email and SMS at this time.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground"
                      onClick={() => handleFollowUpChange(undefined)}
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" />
                      Clear follow-up
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="font-display text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
              <div className="space-y-3">
                {phone && (
                  <Button className="w-full justify-start gap-3" variant="outline" asChild>
                    <a href={`tel:${phoneDigits}`}>
                      <Phone className="w-4 h-4" />
                      Call Lead
                    </a>
                  </Button>
                )}
                {phone && (
                  <Button
                    className="w-full justify-start gap-3"
                    variant="outline"
                    onClick={() => navigate(`/dashboard/messages?lead=${lead.id}`)}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Send SMS
                  </Button>
                )}
                {email && (
                  <Button className="w-full justify-start gap-3" variant="outline" asChild>
                    <a href={`mailto:${email}`}>
                      <Mail className="w-4 h-4" />
                      Send Email
                    </a>
                  </Button>
                )}
              </div>
            </div>

            {/* Lead Info */}
            <div className="bg-muted/30 rounded-2xl border border-border p-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">Lead ID</h3>
              <p className="text-xs text-muted-foreground font-mono break-all">{lead.id}</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LeadDetail;
